import { PageShell } from '@/components/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useListQueryRoute } from '@/lib/use-list-query-route';
import {
  REPLENISHMENT_STATUSES,
  type ReplenishmentStatus,
  type ReplenishmentTransferDto,
  type ReplenishmentTransfersFilter,
  useReplenishmentTransfers,
} from '@/replenishment/queries';

const ALL = '__all__';
const DEFAULT_PAGE_SIZE = 25;

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

const STATUS_VARIANT: Record<
  ReplenishmentStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'outline',
  sent: 'secondary',
  accepted: 'default',
  failed: 'destructive',
  cancelled: 'outline',
};

export const ReplenishmentPage = () => {
  // Plan 10-03 — operator history of storage→sender shipments. URL is
  // the source of truth (ADR-014); the route's `validateSearch` schema
  // coerces page/pageSize from string → number.
  const lq = useListQueryRoute<ReplenishmentTransfersFilter, 'createdAt'>({
    from: '/_protected/replenishment',
    defaultFilter: {
      warehouseId: undefined,
      storageAccountId: undefined,
      senderAccountId: undefined,
      status: undefined,
      from: undefined,
      to: undefined,
    },
    defaultSort: { field: 'createdAt', dir: 'desc' },
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const query = useReplenishmentTransfers({
    filter: lq.filter,
    page: lq.page,
    pageSize: lq.pageSize,
    sort: 'createdAt',
    dir: lq.sort.dir,
  });

  const totalPages = query.data ? Math.max(0, Math.ceil(query.data.total / lq.pageSize)) : 0;

  const filterSlot = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Warehouse ID</span>
        <Input
          className="w-72"
          value={lq.filter.warehouseId ?? ''}
          placeholder="UUID"
          onChange={(e) => {
            lq.updateQuery({ filter: { warehouseId: e.target.value || undefined } });
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Storage account ID</span>
        <Input
          className="w-72"
          value={lq.filter.storageAccountId ?? ''}
          placeholder="UUID"
          onChange={(e) => {
            lq.updateQuery({ filter: { storageAccountId: e.target.value || undefined } });
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Sender account ID</span>
        <Input
          className="w-72"
          value={lq.filter.senderAccountId ?? ''}
          placeholder="UUID"
          onChange={(e) => {
            lq.updateQuery({ filter: { senderAccountId: e.target.value || undefined } });
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Статус</span>
        <div className="w-40">
          <Select
            value={lq.filter.status ?? ALL}
            onValueChange={(v) => {
              lq.updateQuery({
                filter: { status: v === ALL ? undefined : (v as ReplenishmentStatus) },
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все</SelectItem>
              {REPLENISHMENT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>From (ISO)</span>
        <Input
          className="w-56"
          value={lq.filter.from ?? ''}
          placeholder="2026-04-01T00:00:00Z"
          onChange={(e) => {
            lq.updateQuery({ filter: { from: e.target.value || undefined } });
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>To (ISO)</span>
        <Input
          className="w-56"
          value={lq.filter.to ?? ''}
          placeholder="2026-04-30T23:59:59Z"
          onChange={(e) => {
            lq.updateQuery({ filter: { to: e.target.value || undefined } });
          }}
        />
      </label>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          lq.resetQuery();
        }}
      >
        Сбросить
      </Button>
    </div>
  );

  return (
    <div className="space-y-4 p-6">
      <PageShell
        title="Пополнение склада → ботов"
        description="История переводов от storage-аккаунтов к sender-аккаунтам (Plan 10-02)"
      >
        <div className="space-y-3 p-6">
          {filterSlot}

          {query.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : query.isError ? (
            <p className="text-sm text-destructive">Не удалось загрузить переводы.</p>
          ) : query.data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Записей не найдено.</p>
          ) : (
            <ul className="divide-y rounded border">
              {query.data.items.map((row: ReplenishmentTransferDto) => (
                <li key={row.id} className="px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
                    <time
                      className="font-mono text-xs text-muted-foreground"
                      dateTime={row.createdAt}
                      title={row.createdAt}
                    >
                      {formatDate(row.createdAt)}
                    </time>
                    <span className="text-xs text-muted-foreground">
                      склад {row.storageAccountId.slice(0, 8)}… → бот{' '}
                      {row.senderAccountId.slice(0, 8)}…
                    </span>
                    <span className="text-xs">
                      {row.itemsTransferred ?? row.itemsRequested}/{row.itemsRequested} items
                    </span>
                    {row.steamOfferId ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        offer {row.steamOfferId}
                      </span>
                    ) : null}
                  </div>
                  {row.itemNames.length > 0 ? (
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {row.itemNames.slice(0, 5).join(', ')}
                      {row.itemNames.length > 5 ? ` … (+${String(row.itemNames.length - 5)})` : ''}
                    </div>
                  ) : null}
                  {row.error ? (
                    <div className="mt-1 text-xs text-destructive">error: {row.error}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          <footer className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>{query.data ? `Всего: ${String(query.data.total)}` : ''}</span>
            <Pagination
              page={lq.page}
              totalPages={totalPages}
              onPageChange={(page) => {
                lq.updateQuery({ page });
              }}
            />
          </footer>
        </div>
      </PageShell>
    </div>
  );
};
